import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Copy, Check, Download, X, Search, RefreshCw, SlidersHorizontal } from 'lucide-react';
import * as XLSX from 'xlsx';
import authService from '../services/AuthService';
import { API_BASE } from '../consts';

// ═══════════════════════════════════════════════════════════════════════════
// MIP GPT — the Influencer Pro side of MSP GPT.
//   1. Fast    : POST /api/amazon/aip/card-data-keepa/<asin> -> card shows right away (image, title, sales, price, CC)
//   2. Accurate: video numbers only when they're a REAL scrape. Otherwise POST /api/amazon/aip/card-data/<asin>
//                loads in the background and the boxes show "Loading full data…" until it arrives.
//   CC pre-check ("Only with CC"): GET /api/amazon/aip/card-data/<asin>/cc
//   Extras (same as the extension): my videos created, brand messaged, mobile/extension queue
// ═══════════════════════════════════════════════════════════════════════════

export const CARD_BATCH = 7;              // live card-data requests at a time
export const CARD_BATCH_PAUSE_MS = 1000;  // wait between batches

// Onsite Associates Table A (non-Creator-Ads) — default commission when there is no CC campaign.
// Checked in order, first match wins. (Copied from the extension.)
const ONSITE_COMMISSION_RULES = [
    [/luxury.*beauty/i, 4],
    [/gift card|alcohol|wine|beer|spirits|wireless service/i, 0],
    [/furniture|pet|lawn|garden|patio|home improvement|\bhome\b/i, 3],
    [/amazon coins/i, 2.5],
    [/beauty|pantry|musical instrument|industrial|business/i, 2],
    [/\btools?\b/i, 1.75],
    [/music|handmade|digital video|health|household|personal care|sport|kitchen|automotive/i, 1.25],
    [/headphone|clothing|fashion|apparel|watch|jewelry|luggage|shoe|handbag|echo|ring device|cloud cam/i, 1],
    [/outdoor/i, 0.75],
    [/video game|grocery|baby|book|kindle|fire tablet|fire tv|toy|\bpc\b|computer|dvd|blu-ray|television|\btv\b/i, 0.5],
];
export const getOnsiteCommissionRate = (category) => {
    const c = String(category || '');
    for (const [re, rate] of ONSITE_COMMISSION_RULES) if (re.test(c)) return rate;
    return 1; // All Other Categories
};

export const sleep = (ms) => new Promise(r => setTimeout(r, ms));

async function fetchJson(url, opts = {}, tries = 2) {
    for (let attempt = 1; attempt <= tries; attempt++) {
        try {
            const r = await authService.authenticatedFetch(url, opts);
            if ((r.status === 429 || r.status >= 500) && attempt < tries) { await sleep(1500 * attempt); continue; }
            if (!r.ok) return { ok: false, status: r.status };
            const data = await r.json().catch(() => null);
            return { ok: true, data };
        } catch (e) {
            if (attempt < tries) { await sleep(1000 * attempt); continue; }
            return { ok: false, error: e?.message || 'network error' };
        }
    }
    return { ok: false, error: 'unknown' };
}

// ── Campaigns ──
export const isLiveCampaign = (c, now = Date.now()) =>
    (!c.endMs || c.endMs > now) && (c.remainingBudget === null || c.remainingBudget === undefined || c.remainingBudget > 0);
const daysLeftOf = (c, now = Date.now()) => (c.endMs ? Math.ceil((c.endMs - now) / 86400000) : null);

const parseCampaigns = (list) => (list || []).map(c => ({
    campaignId: c.campaignId || '',
    campaignName: c.campaignName || '',
    brandName: c.brandName || '',
    commissionPercentage: c.commissionPercentage ?? 0,
    remainingBudget: c.remainingBudget ?? null,
    endMs: c.endMs || 0,
}));

// ── Live card data ──
// Real scrape only. Keepa-estimated (is_keepa) or blocked results are NOT shown as numbers.
const normalizeInf = (raw) => {
    if (!raw || raw === 'err' || typeof raw !== 'object') return null;
    const u = raw.upper_carousel || {};
    const b = raw.bottom_carousel || {};
    const names = new Set();
    [...(u.influencer_names || []), ...(b.influencer_names || [])].forEach(n => names.add(typeof n === 'string' ? n.toLowerCase() : JSON.stringify(n)));
    return {
        upperVid: +(u.avg_videos_per_fetch || 0).toFixed(1),
        upperInf: +(u.avg_influencer_per_fetch || 0).toFixed(1),
        upperUnique: u.total_unique_videos ?? null,
        upperUniqueInf: u.total_unique_influencer_videos ?? null,
        bottomVid: +(b.avg_videos_per_fetch || 0).toFixed(1),
        bottomInf: +(b.avg_influencer_per_fetch || 0).toFixed(1),
        bottomUnique: b.total_unique_videos ?? null,
        bottomUniqueInf: b.total_unique_influencer_videos ?? null,
        totalInf: raw.total_inf_videos ?? null,
        influencers: names.size,
        isKeepa: raw.is_keepa === true,
        blocked: (raw.fetches_blocked || 0) > 0,
    };
};

export async function fetchCardData(asin) {
    const res = await fetchJson(`${API_BASE}/api/amazon/aip/card-data/${asin}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaign: null, skip_quick: true }),   // product details come from MSP GPT already
    });
    if (!res.ok) return { cardStatus: 'failed', inf: null, campaigns: [], quick: null };
    const d = res.data || {};
    const inf = normalizeInf(d.inf);
    const live = !!inf && !inf.isKeepa && !inf.blocked;
    return {
        cardStatus: live ? 'live' : 'failed',
        inf: live ? inf : null,
        campaigns: parseCampaigns(d.cc?.campaigns),
        quick: d.quick || null,
    };
}

export async function fetchCCOnly(asin) {
    const res = await fetchJson(`${API_BASE}/api/amazon/aip/card-data/${asin}/cc`);
    return res.ok ? parseCampaigns(res.data?.cc?.campaigns) : [];
}

// ── Extras: same endpoints as the extension (mobile app marks -> extension runs the queue) ──
export async function fetchBrandMessaged(brand) {
    const res = await fetchJson(`${API_BASE}/api/aip/chat-brand-meta/check?brand_name=${encodeURIComponent(brand)}`);
    return res.ok ? !!res.data?.messaged : null;
}
export async function fetchQueued(queueType) {
    const res = await fetchJson(`${API_BASE}/api/aip/asin-queue?queue_type=${queueType}&status=pending`);
    return new Set(res.ok && Array.isArray(res.data) ? res.data.map(it => (it.asin || '').toUpperCase()) : []);
}
// Items the extension couldn't process (e.g. campaign full) -> { ASIN: "reason" }
export async function fetchQueueFailed() {
    const out = {};
    for (const type of ['outreach', 'accept']) {   // accept last, so its reason wins
        const res = await fetchJson(`${API_BASE}/api/aip/asin-queue?queue_type=${type}&status=failed`);
        if (res.ok && Array.isArray(res.data)) res.data.forEach(it => {
            out[(it.asin || '').toUpperCase()] = `${type === 'accept' ? 'Accept' : 'Outreach'} failed${it.result_reason ? `: ${it.result_reason}` : ''}`;
        });
    }
    return out;
}
export async function queueAsins(queueType, items) {
    const res = await fetchJson(`${API_BASE}/api/aip/asin-queue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ queue_type: queueType, items }),
    }, 1);
    return res.ok;
}
// "Videos Created" (same as the extension badge): one call for all ASINs.
// Returns { asin: count }, or null when no storefront is set / the call fails.
export async function fetchMyVideoCounts() {
    const [sf, counts] = await Promise.all([
        fetchJson(`${API_BASE}/api/aip/my-storefront`),
        fetchJson(`${API_BASE}/api/aip/my-asin-counts`),
    ]);
    if (!sf.ok || !sf.data?.storefront_url || !counts.ok) return null;
    return counts.data?.asin_counts || {};
}
// Campaign fill % we already know (same data as Power Search). Unknown campaigns are just missing.
// Returns { campaignId: { pct_full, not_found } }
export async function fetchCampaignStatus(ids) {
    if (!ids.length) return {};
    const res = await fetchJson(`${API_BASE}/api/amazon/aip/campaign-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
    });
    return res.ok ? (res.data?.status || {}) : {};
}
// Runs fn over items, `limit` at a time
const mapLimit = async (items, limit, fn) => {
    const queue = [...items];
    await Promise.all(Array.from({ length: Math.min(limit, queue.length) }, async () => {
        while (queue.length) await fn(queue.shift());
    }));
};

// Fast first pass. Returns a card record, or null (no data / no live campaign in "Only with CC").
// cardStatus 'live' when the cached video data is a real scrape, otherwise 'loading' (full data loads next).
export async function loadQuick(asin, ccOnly) {
    let ccCampaigns = null;
    if (ccOnly) {
        ccCampaigns = await fetchCCOnly(asin);
        if (!ccCampaigns.some(c => isLiveCampaign(c))) return null;
    }
    const res = await fetchJson(`${API_BASE}/api/amazon/aip/card-data-keepa/${asin}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaign: null }),
    });
    const q = res.ok ? res.data?.quick : null;
    if (!q) return null;
    const inf = normalizeInf(res.data?.inf);
    const real = !!inf && !inf.isKeepa && !inf.blocked;
    let campaigns = parseCampaigns(res.data?.cc?.campaigns);
    if (!campaigns.length && ccCampaigns) campaigns = ccCampaigns;
    return {
        asin,
        title: q.title || '',
        brand: q.brand || '',
        image: q.image || '',
        category: q.category || '',
        price: q.price > 0 ? q.price : null,
        monthlySold: q.monthly_sold > 0 ? q.monthly_sold : null,
        salesVerified: q.is_verified !== false,
        variants: q.variants_count ?? null,
        inf: real ? inf : null,
        campaigns,
        cardStatus: real ? 'live' : 'loading',
    };
}

export const mergeCard = (rec, card) => {
    const q = card.quick || {};
    return {
        ...rec,
        inf: card.inf || rec.inf || null,
        campaigns: card.campaigns?.length ? card.campaigns : (rec.campaigns || []),
        cardStatus: card.cardStatus,
        title: rec.title || q.title || '',
        image: rec.image || q.image || '',
        price: rec.price ?? (q.price > 0 ? q.price : null),
        monthlySold: rec.monthlySold ?? (q.monthly_sold > 0 ? q.monthly_sold : null),
        variants: rec.variants ?? q.variants_count ?? null,
    };
};

// ── Metrics: every one can be filtered with min / max ──
export const infVideoCount = (inf) => (inf ? (inf.totalInf ?? null) : null);
export const totalVideoCount = (inf) => (inf ? +((inf.upperVid || 0) + (inf.bottomVid || 0)).toFixed(1) : null);
const numOrNull = (x) => (x === '' || x === null || x === undefined || isNaN(Number(x)) ? null : Number(x));

// full: true -> needs the full (live) card data; checked once it loads
// extra: true -> loaded separately on the results page; skipped until it's known
export const INF_METRICS = [
    { key: 'commission', label: 'Commission %', get: (p, d) => d.comm },
    { key: 'days_left', label: 'CC days left', get: (p, d) => d.daysLeft },
    { key: 'budget', label: 'CC budget left $', get: (p, d) => d.budget },
    { key: 'campaigns', label: 'CC campaigns', get: (p, d) => d.campaignCount },
    { key: 'upper_videos', label: 'Upper videos', get: (p) => p.inf?.upperVid ?? null, full: true },
    { key: 'bottom_videos', label: 'Bottom videos', get: (p) => p.inf?.bottomVid ?? null, full: true },
    { key: 'total_videos', label: 'Total videos', get: (p) => totalVideoCount(p.inf), full: true },
    { key: 'influencer_videos', label: 'Influencer videos', get: (p) => infVideoCount(p.inf), full: true },
    { key: 'est_income', label: 'Est. income $/mo', get: (p, d) => d.income, full: true },
    { key: 'profit_12mo', label: '12-mo profit $', get: (p, d) => d.profit12, full: true },
    { key: 'breakeven_days', label: 'Breakeven days', get: (p, d) => d.breakevenDays, full: true },
    { key: 'revenue', label: 'Revenue $/mo', get: (p, d) => d.revenue },
    { key: 'monthly_sold', label: 'Sales / mo', get: (p) => p.monthlySold },
    { key: 'price', label: 'Price $', get: (p) => p.price },
    { key: 'variants', label: 'Variants', get: (p) => p.variants },
    { key: 'videos_created', label: 'My videos created', get: (p, d) => d.videosCreated, extra: true },
    { key: 'pct_full', label: 'CC % full', get: (p, d) => d.pctFull, extra: true },   // unknown = kept
];
const METRIC_BY_KEY = Object.fromEntries(INF_METRICS.map(m => [m.key, m]));
const rangeSet = (r) => !!r && (numOrNull(r.min) !== null || numOrNull(r.max) !== null);

// page_filters from the question -> { metric: { min, max } }  (also reads older saved searches)
export function rangesFromFilters(f = {}) {
    const out = {};
    (f?.page_filters || []).forEach(pf => {
        const key = pf?.metric === 'influencers' ? 'influencer_videos' : pf?.metric;   // older searches
        if (pf && METRIC_BY_KEY[key]) out[key] = { min: pf.min ?? '', max: pf.max ?? '' };
    });
    const legacy = { max_upper_videos: 'upper_videos', max_bottom_videos: 'bottom_videos', max_influencer_videos: 'influencer_videos', max_total_videos: 'total_videos' };
    Object.entries(legacy).forEach(([k, m]) => {
        if (f?.[k] !== undefined && f?.[k] !== null && !out[m]) out[m] = { min: '', max: f[k] };
    });
    return out;
}
export const hasRanges = (r) => Object.values(r || {}).some(rangeSet);
export const needsFullData = (r) => Object.entries(r || {}).some(([k, x]) => METRIC_BY_KEY[k]?.full && rangeSet(x));

// full=false skips metrics that need the full data (they're checked once it loads)
export function passesRanges(p, d, ranges, full = true) {
    for (const [k, r] of Object.entries(ranges || {})) {
        const m = METRIC_BY_KEY[k];
        if (!m || !rangeSet(r) || (m.full && !full)) continue;
        const mn = numOrNull(r.min), mx = numOrNull(r.max);
        const val = m.get(p, d);
        if (m.extra && val === undefined) continue;          // not loaded yet -> don't hide it
        if (val === null || val === undefined || isNaN(val)) return false;
        if (mn !== null && val < mn) return false;
        if (mx !== null && val > mx) return false;
    }
    return true;
}

// Same estimate as the AIP CSV Scan, with the live influencer count
export function estIncome(d, comm) {
    const price = d.price || 0;
    if (!price || !comm || !d.inf) return null;
    const sold = d.monthlySold > 0 ? d.monthlySold : 30;
    const hasUpper = d.inf.upperVid > 0;
    const infCount = d.inf.totalInf || 0;
    const watchRate = 0.015 * (hasUpper ? 2 : 1);
    return sold * watchRate * (1 / (infCount + 1)) * price * (comm / 100);
}

// Campaign that pays most (most days left on a tie)
const pickBest = (list, now) => {
    let best = null;
    list.forEach(c => {
        if (!best || (c.commissionPercentage ?? -1) > (best.commissionPercentage ?? -1)
            || ((c.commissionPercentage ?? -1) === (best.commissionPercentage ?? -1) && (daysLeftOf(c, now) ?? -1) > (daysLeftOf(best, now) ?? -1))) best = c;
    });
    return best;
};

// ccState: 'live' (usable CC) | 'full' (only full campaigns) | 'expired' (only ended / no budget) | 'none'
// No usable campaign -> commission falls back to the onsite rate. Rows are never dropped here.
export function deriveInfluencer(p) {
    const now = Date.now();
    const all = p.campaigns || [];
    const isFull = (c) => c.pctFull != null && c.pctFull >= 100;   // pctFull comes from our DB when known
    const live = all.filter(c => isLiveCampaign(c, now));
    const usable = live.filter(c => !isFull(c));
    const best = pickBest(usable, now);
    const ccState = best ? 'live' : live.length ? 'full' : all.length ? 'expired' : 'none';
    const shown = best || pickBest(live, now) || pickBest(all, now);   // what the CC box shows, even when full / expired
    const onsiteRate = getOnsiteCommissionRate(p.category);
    const comm = best ? (best.commissionPercentage ?? 0) : onsiteRate;
    const income = estIncome(p, comm);   // CSV Scan / extension formula; comm = CC commission, or onsite rate when no CC
    // Profit & breakeven: the influencer buys the product at its Amazon price
    const profit12 = income !== null && p.price ? income * 12 - p.price : null;
    const breakevenDays = income > 0 && p.price ? Math.ceil(p.price / (income / 30)) : null;
    return {
        hasCC: !!best,
        ccState,
        best,
        shown,
        campaignCount: usable.length,
        comm,
        onsiteRate,
        daysLeft: best ? daysLeftOf(best, now) : null,
        pctFull: shown ? shown.pctFull : null,                   // undefined = not in our DB yet
        budget: best ? best.remainingBudget ?? null : null,
        income,
        profit12,
        breakevenDays,
        revenue: p.price && p.monthlySold ? p.price * p.monthlySold : null,
    };
}

const ccCampaignUrl = (id) => `https://affiliate-program.amazon.com/p/connect/request?adId=${encodeURIComponent(id)}&campaignId=${encodeURIComponent(id)}&recc=0&early-acc=0`;
const ccSearchUrl = (asin) => `https://affiliate-program.amazon.com/p/connect/requests?status=opportunity&type=affiliate-plus&sortBy=recommended_for_you&keyword=${asin}`;
const fmtMoney = (v, digits = 0) => (v === null || v === undefined || isNaN(v)) ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
const fmtNum = (v) => (v === null || v === undefined || isNaN(v)) ? '—' : Number(v).toLocaleString();
const brandOf = (vid, inf) => Math.max(0, +(vid - inf).toFixed(1));

// ── UI bits ──
const CopyBtn = ({ text }) => {
    const [done, setDone] = useState(false);
    return (
        <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(text || ''); setDone(true); setTimeout(() => setDone(false), 1200); }}
            className="text-gray-400 hover:text-gray-700" title="Copy">
            {done ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
        </button>
    );
};

const Box = ({ label, value, sub, className = 'bg-gray-50 border-gray-200 text-gray-800', title, onClick, children }) => (
    <div title={title} onClick={onClick}
        className={`rounded-lg p-1.5 border text-center ${onClick ? 'cursor-pointer hover:shadow-sm hover:-translate-y-px transition' : ''} ${className}`}>
        <div className="text-[10px] font-semibold whitespace-nowrap opacity-70">{label}</div>
        <div className="font-bold text-sm leading-tight">{value ?? '—'}</div>
        {sub && <div className="text-[9px] whitespace-nowrap opacity-70">{sub}</div>}
        {children}
    </div>
);

// Box placeholder while the full data loads
const LoadingBox = ({ label }) => (
    <div className="rounded-lg p-1.5 border border-gray-200 bg-white text-center">
        <div className="text-gray-400 text-[10px] font-semibold whitespace-nowrap">{label}</div>
        <div className="mx-auto mt-1 h-3.5 w-10 rounded bg-gray-200 animate-pulse" />
        <div className="mx-auto mt-1 h-2 w-14 rounded bg-gray-100 animate-pulse" />
    </div>
);

const CCBox = ({ d, onOpen }) => {
    if (d.ccState === 'none') {
        return <Box label="Commission" value={`${d.onsiteRate}%`} sub="Onsite · no CC" className="bg-gray-50 border-gray-200 text-gray-700" />;
    }
    if (d.ccState !== 'live') {   // full / expired CC: you'd earn the onsite rate
        const isFullCC = d.ccState === 'full';
        return (
            <Box
                label={isFullCC ? '🤝 CC full' : '🤝 CC expired'}
                value={`${d.onsiteRate}% onsite`}
                onClick={onOpen}
                title={`The CC campaign${d.shown?.commissionPercentage != null ? ` (${d.shown.commissionPercentage}%)` : ''} is ${isFullCC ? 'full' : 'expired'}, so you'd earn the ${d.onsiteRate}% onsite rate. Click to see the campaigns.`}
                className={isFullCC ? 'bg-red-50 border-red-300 text-red-800' : 'bg-gray-100 border-gray-300 text-gray-600'}
            />
        );
    }
    const hot = d.comm >= 20;
    const full = d.pctFull != null && d.pctFull >= 100;
    const days = d.daysLeft === null ? 'no end date' : `${d.daysLeft}d left`;
    return (
        <Box
            label={d.campaignCount > 1 ? `🤝 CC ×${d.campaignCount}` : '🤝 CC'}
            value={`${d.comm}%`}
            sub={full ? '🔴 Full' : d.pctFull != null ? `${days} · ${d.pctFull}% full` : days}
            onClick={onOpen}
            title={full ? 'This campaign is full (from our data). Open to see the others.' : 'See all Creator Connections campaigns'}
            className={full
                ? 'bg-red-50 border-red-300 text-red-800'
                : hot
                    ? 'bg-gradient-to-b from-emerald-500 to-green-600 border-green-700 text-white'
                    : 'bg-gradient-to-b from-emerald-50 to-green-100 border-green-300 text-green-800'}
        />
    );
};

const CampaignsModal = ({ p, onClose }) => {
    const now = Date.now();
    const camps = [...(p.campaigns || [])].sort((a, b) => (b.commissionPercentage ?? 0) - (a.commissionPercentage ?? 0));
    return (
        <div className="fixed inset-0 z-[300] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3 px-5 py-4 border-b">
                    <div className="min-w-0">
                        <div className="font-bold text-gray-800">Creator Connections campaigns</div>
                        <div className="text-xs text-gray-500 truncate">{p.title} · {p.asin}</div>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500"><X size={16} /></button>
                </div>
                <div className="overflow-y-auto p-4 space-y-2">
                    {camps.map((c, i) => {
                        const live = isLiveCampaign(c, now);
                        const d = daysLeftOf(c, now);
                        return (
                            <div key={c.campaignId || i} className={`flex items-center gap-3 border rounded-lg px-3 py-2 ${live ? 'border-green-200 bg-green-50/40' : 'border-gray-200 bg-gray-50 opacity-70'}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs font-bold text-blue-700 uppercase truncate">{c.brandName || p.brand || '—'}</div>
                                    <div className="text-sm font-semibold text-gray-800 truncate" title={c.campaignName}>{c.campaignName || 'Campaign'}</div>
                                    <div className="text-[11px] text-gray-500 flex flex-wrap gap-x-3">
                                        <span>Budget left: {c.remainingBudget === null || c.remainingBudget === undefined ? '—' : fmtMoney(c.remainingBudget)}</span>
                                        <span>{d === null ? 'No end date' : d > 0 ? `${d} days left` : 'Ended'}</span>
                                        <span className={`font-bold ${live ? 'text-green-700' : 'text-gray-500'}`}>{live ? 'Live' : 'Expired'}</span>
                                        {c.pctFull != null && (
                                            <span className={`font-bold ${c.pctFull >= 100 ? 'text-red-600' : 'text-gray-600'}`}>{c.pctFull >= 100 ? '🔴 Full' : `${c.pctFull}% full`}</span>
                                        )}
                                    </div>
                                </div>
                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${c.commissionPercentage >= 20 ? 'bg-green-600 text-white' : c.commissionPercentage >= 10 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                                    {c.commissionPercentage}%
                                </span>
                                {c.campaignId && (
                                    <a href={ccCampaignUrl(c.campaignId)} target="_blank" rel="noopener noreferrer"
                                        className="px-2.5 py-1.5 rounded-md bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs font-semibold whitespace-nowrap">Open CC</a>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const InfluencerCard = ({ p, d, index, amazonDomain, currency, onRetry, queued = {}, onQueue }) => {
    const [showCamps, setShowCamps] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [busy, setBusy] = useState('');
    const amazonUrl = `https://www.amazon${amazonDomain}/dp/${p.asin}`;
    const inf = p.inf;
    const loading = p.cardStatus === 'loading';
    const unavailable = !loading && !inf;

    const retry = async () => {
        if (!onRetry || retrying) return;
        setRetrying(true);
        await onRetry(p.asin);
        setRetrying(false);
    };

    // Queue for the extension (same queue the mobile app uses)
    const qBtn = (type, label, done) => (
        <button
            disabled={done || !!busy}
            onClick={async () => { setBusy(type); await onQueue?.(type); setBusy(''); }}
            title="Added to your queue: runs next time the extension is opened on desktop. If it can't be done (e.g. the campaign is full), the reason shows here."
            className={`px-1.5 py-0.5 rounded border font-bold transition ${done ? 'bg-green-50 border-green-300 text-green-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'} disabled:cursor-default`}
        >
            {done ? '✓ Queued' : busy === type ? 'Queuing…' : label}
        </button>
    );

    const videoBox = (label, vid, infCount, cls) => (loading ? <LoadingBox label={label} /> : (
        <Box label={label} value={inf ? `${vid}` : '—'} sub={inf ? `${brandOf(vid, infCount)} brand · ${infCount} inf` : null} className={cls} />
    ));

    return (
        <div className="border border-gray-200 rounded-lg bg-white p-3 hover:shadow-md transition">
            <div className="flex gap-3 items-start">
                <div className="text-xs font-bold text-gray-300 w-6 pt-1 text-right flex-shrink-0">{index + 1}</div>
                <div className="flex flex-col items-center gap-1 flex-shrink-0 w-[76px]">
                    <a href={amazonUrl} target="_blank" rel="noopener noreferrer" className="relative group">
                        <img src={p.image || '/noimage.png'} alt=""
                            className="w-16 h-16 object-contain rounded border-2 border-gray-200 bg-white transition-all duration-300 group-hover:scale-[2.5] group-hover:z-[100] group-hover:shadow-2xl group-hover:border-blue-400 relative"
                            onError={(e) => { e.target.src = '/noimage.png'; }} />
                    </a>
                    <a href={amazonUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[10px] font-semibold text-gray-600 hover:text-gray-900" title="Open on Amazon">
                        <img src="/icons/amazon.png" alt="" className="w-4 h-4 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                        Amazon
                    </a>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                        <a href={amazonUrl} target="_blank" rel="noopener noreferrer" className="flex-1 font-semibold text-sm text-gray-800 hover:text-blue-700 line-clamp-1" title={p.title}>
                            {p.title || p.asin}
                        </a>
                        {d.best?.campaignId ? (
                            <a href={ccCampaignUrl(d.best.campaignId)} target="_blank" rel="noopener noreferrer"
                                className="flex-shrink-0 px-2 py-0.5 rounded-md bg-gradient-to-r from-blue-600 to-purple-600 text-white text-[11px] font-bold">Open CC</a>
                        ) : (
                            <a href={ccSearchUrl(p.asin)} target="_blank" rel="noopener noreferrer"
                                className="flex-shrink-0 px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-[11px] font-bold hover:bg-gray-200">Find CC</a>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-0.5">
                        {p.brand && <span className="font-semibold text-blue-700 uppercase">{p.brand}</span>}
                        <span className="flex items-center gap-1">ASIN: {p.asin} <CopyBtn text={p.asin} /></span>
                        {p.category && <span>{p.category}</span>}
                        {p.variants > 0 && <span>{p.variants} variants</span>}
                        {loading && <span className="flex items-center gap-1 text-blue-600 font-semibold"><RefreshCw size={11} className="animate-spin" /> Loading full data…</span>}
                        {unavailable && (
                            <button onClick={retry} disabled={retrying} className="flex items-center gap-1 text-amber-700 font-semibold hover:underline disabled:opacity-60">
                                <RefreshCw size={11} className={retrying ? 'animate-spin' : ''} /> {p.cardStatus === 'stopped' ? 'Full data not loaded' : 'Full data unavailable'} · Retry
                            </button>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1 text-[11px]">
                        <span
                            title={d.videosCreated === null ? 'Set your storefront in the extension (Videos Created) to see this' : 'Videos you already recorded for this ASIN'}
                            className={`px-1.5 py-0.5 rounded border font-semibold ${d.videosCreated > 0 ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-gray-50 border-gray-200 text-gray-600'}`}
                        >
                            🎥 My videos: {d.videosCreated === undefined ? '…' : d.videosCreated ?? '—'}
                        </span>
                        {p.brand && d.ccState !== 'none' && (
                            <span className={`px-1.5 py-0.5 rounded border font-semibold ${d.messaged ? 'bg-green-50 border-green-200 text-green-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                                {d.messaged === undefined ? 'Checking brand…' : d.messaged ? '✓ Messaged brand' : d.messaged === false ? '📨 Not messaged yet' : 'Brand: —'}
                            </span>
                        )}
                        {queued.failed && !queued.accept && !queued.outreach && (
                            <span className="px-1.5 py-0.5 rounded border font-semibold bg-red-50 border-red-200 text-red-700 max-w-[260px] truncate" title={queued.failed}>
                                ✗ {queued.failed}
                            </span>
                        )}
                        {d.hasCC && qBtn('accept', '⚡ Accept campaign', queued.accept)}
                        {d.ccState !== 'none' && qBtn('outreach', '🔔 Remind outreach', queued.outreach)}
                    </div>
                    <div className="grid grid-cols-4 lg:grid-cols-8 gap-1.5 mt-2">
                        <CCBox d={d} onOpen={() => setShowCamps(true)} />
                        {videoBox('Upper', inf?.upperVid, inf?.upperInf, 'bg-amber-50 border-amber-200 text-amber-800')}
                        {videoBox('Bottom', inf?.bottomVid, inf?.bottomInf, 'bg-pink-50 border-pink-200 text-pink-800')}
                        {loading ? <LoadingBox label="Inf. videos" /> : (
                            <Box label="Inf. videos" value={inf ? fmtNum(inf.totalInf) : '—'} className="bg-purple-50 border-purple-200 text-purple-800" />
                        )}
                        <Box label="Sales / mo" value={p.monthlySold ? `${fmtNum(p.monthlySold)}+` : null} sub={p.monthlySold && p.salesVerified === false ? 'estimated' : null} />
                        <Box label="Price" value={p.price ? `${currency}${Number(p.price).toFixed(2)}` : null} className="bg-green-50 border-green-200 text-green-800" />
                        {loading ? <LoadingBox label="Est. income" /> : (
                            <Box label="Est. income" value={d.income !== null ? `${fmtMoney(d.income, 2)}/mo` : null}
                                sub={d.profit12 !== null ? `12-mo profit ${fmtMoney(d.profit12)}` : null}
                                className={d.profit12 !== null && d.profit12 < 0 ? 'bg-red-50 border-red-200 text-red-800' : 'bg-teal-50 border-teal-200 text-teal-800'} />
                        )}
                        {loading ? <LoadingBox label="Breakeven" /> : (
                            <Box label="Breakeven" value={d.breakevenDays !== null ? `${fmtNum(d.breakevenDays)} days` : null}
                                sub={d.breakevenDays !== null ? 'to earn back the price' : null}
                                className={d.breakevenDays !== null && d.breakevenDays <= 30 ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-gray-50 border-gray-200 text-gray-800'} />
                        )}
                    </div>
                </div>
            </div>
            {showCamps && <CampaignsModal p={p} onClose={() => setShowCamps(false)} />}
        </div>
    );
};

// ── Results: search, CC pills, min/max filters for every metric, sort, export, cards ──
const EMPTY_INF_VIEW = { search: '', cc: 'all', ranges: {}, hideExpired: false, hideFull: false, bestPerBrand: false, sort: 'default', messaged: 'all', queue: 'all' };

const INF_SORTS = [
    ['default', 'Sort: as found'],
    ['commission:desc', 'Commission: high → low'],
    ['est_income:desc', 'Est. income: high → low'],
    ['profit_12mo:desc', '12-mo profit: high → low'],
    ['breakeven_days:asc', 'Breakeven: fastest first'],
    ['days_left:desc', 'Days left: high → low'],
    ['budget:desc', 'Budget left: high → low'],
    ['total_videos:asc', 'Total videos: low → high'],
    ['upper_videos:asc', 'Upper videos: low → high'],
    ['bottom_videos:asc', 'Bottom videos: low → high'],
    ['influencer_videos:asc', 'Influencer videos: low → high'],
    ['monthly_sold:desc', 'Sales/mo: high → low'],
    ['revenue:desc', 'Revenue: high → low'],
    ['price:asc', 'Price: low → high'],
    ['price:desc', 'Price: high → low'],
    ['videos_created:asc', 'My videos: low → high'],
];

const rangeText = (m, r) => {
    const mn = r?.min === '' || r?.min === null || r?.min === undefined ? null : r.min;
    const mx = r?.max === '' || r?.max === null || r?.max === undefined ? null : r.max;
    if (mn === null && mx === null) return null;
    if (mn !== null && mx !== null) return `${m.label}: ${mn}–${mx}`;
    return mn !== null ? `${m.label} ≥ ${mn}` : `${m.label} ≤ ${mx}`;
};

// ── One-time tip: MIP GPT vs Power Search (modal) ──
const TIP_KEY = 'mipgpt_powersearch_tip_hidden';

const PowerSearchTip = () => {
    const [show, setShow] = useState(() => {
        try { return localStorage.getItem(TIP_KEY) !== '1'; } catch { return true; }
    });
    useEffect(() => {
        if (!show) return;
        const onKey = (e) => { if (e.key === 'Escape') setShow(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [show]);
    if (!show) return null;
    const neverAgain = () => {
        try { localStorage.setItem(TIP_KEY, '1'); } catch { }
        setShow(false);
    };
    return (
        <div className="fixed inset-0 z-[300] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShow(false)}>
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => setShow(false)} title="Close"
                    className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-700">
                    <X size={16} />
                </button>
                <div className="text-3xl">💡</div>
                <div className="mt-2 font-bold text-gray-800 text-lg">Pick the right tool for the job</div>
                <ul className="mt-3 space-y-2 text-sm text-gray-700">
                    <li>📈 <strong>MIP GPT</strong>: find products worth <strong>investing</strong> in.</li>
                    <li>🤝 <strong>Power Search 2.0</strong>: our best <strong>Creator Connections</strong> tool. Use it for non-full campaigns, CC research and bulk outreach.</li>
                    <li>🆕 <strong>Power Search 1.0</strong>: spot new opportunities and new campaigns.</li>
                </ul>
                <p className="mt-3 text-xs text-gray-500">
                    You'll still see CC data and can accept campaigns or queue outreach here, but Power Search 2.0 is more accurate for campaign research.
                </p>
                <div className="mt-3 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                    📍 <strong>Note:</strong> Power Search 2.0 and Power Search 1.0 are in our Chrome extension, on the Creator Connections page.
                </div>
                <div className="flex items-center justify-between gap-2 mt-5">
                    <button onClick={neverAgain} className="text-xs text-red-400 hover:text-red-700 font-semibold">
                        Never show again
                    </button>
                    <button onClick={() => setShow(false)}
                        className="px-5 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-semibold hover:opacity-90">
                        Got it
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Results pages (keeps the page light with many results) ──
export const PAGE_SIZE = 200;
export const Pager = ({ page, total, onPage }) => {
    const pages = Math.ceil(total / PAGE_SIZE);
    if (pages <= 1) return null;
    const go = (n) => { onPage(n); window.scrollTo({ top: 0, behavior: 'smooth' }); };
    const btn = 'px-3 py-1 rounded-md text-xs font-semibold border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40';
    return (
        <div className="flex items-center justify-center gap-2 py-2 text-xs text-gray-600">
            <button className={btn} disabled={page === 0} onClick={() => go(page - 1)}>‹ Prev</button>
            <span>Page {page + 1} of {pages} · {page * PAGE_SIZE + 1}–{Math.min(total, (page + 1) * PAGE_SIZE)} of {total}</span>
            <button className={btn} disabled={page >= pages - 1} onClick={() => go(page + 1)}>Next ›</button>
        </div>
    );
};

export const InfluencerResults = ({ products, amazonDomain, currency, fileName = 'mip_gpt', filters, onRetry }) => {
    const [v, setV0] = useState(() => ({ ...EMPTY_INF_VIEW, cc: filters?.has_cc ? 'cc' : 'all', ranges: rangesFromFilters(filters) }));
    const [showFilters, setShowFilters] = useState(true);
    const [copied, setCopied] = useState(false);
    const [page, setPage] = useState(0);
    useEffect(() => { setPage(0); }, [v]);   // new filters / sort -> back to page 1
    const setV = (patch) => setV0(prev => ({ ...prev, ...patch }));
    const setRange = (k, side, val) => setV0(prev => ({
        ...prev, ranges: { ...prev.ranges, [k]: { ...(prev.ranges[k] || { min: '', max: '' }), [side]: val } },
    }));
    const clearRange = (k) => setV0(prev => { const r = { ...prev.ranges }; delete r[k]; return { ...prev, ranges: r }; });

    // ── Extras: my videos created, brand messaged, mobile/extension queue ──
    const [vidCounts, setVidCounts] = useState(undefined);   // undefined = loading, null = no storefront, {asin: n}
    const [msgMap, setMsgMap] = useState({});                // brand (lowercase) -> true | false | null
    const [queued, setQueued] = useState({ accept: new Set(), outreach: new Set() });
    const [failedMap, setFailedMap] = useState({});          // ASIN -> "Accept failed: campaign full"
    const [campStatus, setCampStatus] = useState({});        // campaignId -> { pct_full, not_found }
    const askedBrandsRef = useRef(new Set());
    const askedCampsRef = useRef(new Set());
    useEffect(() => {
        fetchMyVideoCounts().then(setVidCounts).catch(() => setVidCounts(null));
        Promise.all([fetchQueued('accept'), fetchQueued('outreach')]).then(([accept, outreach]) => setQueued({ accept, outreach }));
        fetchQueueFailed().then(setFailedMap).catch(() => { });
    }, []);
    useEffect(() => {
        const brands = new Map();
        products.forEach(p => {
            const b = (p.brand || '').trim();
            if (b && !askedBrandsRef.current.has(b.toLowerCase())) brands.set(b.toLowerCase(), b);
        });
        brands.forEach((_, k) => askedBrandsRef.current.add(k));
        mapLimit([...brands], 4, async ([k, b]) => { const x = await fetchBrandMessaged(b); setMsgMap(m => ({ ...m, [k]: x })); });
        // Campaign fill % from our DB (500 ids per call)
        const ids = [...new Set(products.flatMap(p => (p.campaigns || []).map(c => c.campaignId)).filter(Boolean))]
            .filter(id => !askedCampsRef.current.has(id));
        ids.forEach(id => askedCampsRef.current.add(id));
        const chunks = [];
        for (let i = 0; i < ids.length; i += 500) chunks.push(ids.slice(i, i + 500));
        mapLimit(chunks, 2, async (chunk) => { const st = await fetchCampaignStatus(chunk); setCampStatus(m => ({ ...m, ...st })); });
    }, [products]);
    // Campaigns with what we know: not_found ones are dropped (like Power Search), the rest get their fill %
    const withStatus = (p) => {
        if (!p.campaigns?.length) return p;
        const campaigns = p.campaigns
            .filter(c => !campStatus[c.campaignId]?.not_found)
            .map(c => (campStatus[c.campaignId]?.pct_full != null ? { ...c, pctFull: campStatus[c.campaignId].pct_full } : c));
        return { ...p, campaigns };
    };
    const isQueued = (asin) => queued.accept.has(asin.toUpperCase()) || queued.outreach.has(asin.toUpperCase());
    const queue = async (type, list) => {
        const items = list.map(p => ({ asin: p.asin, brand_name: p.brand || '' }));
        if (!items.length || !(await queueAsins(type, items))) return;
        setQueued(q => ({ ...q, [type]: new Set([...q[type], ...items.map(i => i.asin.toUpperCase())]) }));
    };

    const rows = useMemo(() => products.map(p0 => {
        const p = withStatus(p0);
        return {
            p,
            d: {
                ...deriveInfluencer(p),
                videosCreated: vidCounts === undefined ? undefined : vidCounts === null ? null : (vidCounts[p.asin] ?? 0),
                messaged: p.brand ? msgMap[p.brand.trim().toLowerCase()] : null,
            },
        };
    }), [products, vidCounts, msgMap, campStatus]);
    const loadingCount = rows.filter(r => r.p.cardStatus === 'loading').length;
    const activeRanges = INF_METRICS.map(m => ({ m, text: rangeText(m, v.ranges[m.key]) })).filter(x => x.text);

    const visible = useMemo(() => {
        const q = v.search.trim().toLowerCase();
        let out = rows.filter(({ p, d }) => {
            if (v.cc === 'cc' && !d.hasCC) return false;
            if (v.cc === 'nocc' && d.hasCC) return false;
            if (v.hideExpired && d.ccState === 'expired') return false;
            if (v.hideFull && d.ccState === 'full') return false;
            if (v.messaged === 'yes' && d.messaged !== true) return false;
            if (v.messaged === 'no' && d.messaged !== false) return false;
            if (v.queue === 'queued' && !isQueued(p.asin)) return false;
            if (v.queue === 'not' && isQueued(p.asin)) return false;
            if (v.queue === 'failed' && !failedMap[p.asin.toUpperCase()]) return false;
            if (q && !`${p.asin} ${p.title} ${p.brand} ${p.category} ${(p.campaigns || []).map(c => `${c.campaignName} ${c.brandName}`).join(' ')}`.toLowerCase().includes(q)) return false;
            // while full data loads, only check what's known; the rest is checked once it arrives
            return passesRanges(p, d, v.ranges, p.cardStatus !== 'loading');
        });
        if (v.bestPerBrand) {
            const best = new Map();
            out.forEach(r => {
                const key = (r.p.brand || r.p.asin).toLowerCase();
                const cur = best.get(key);
                if (!cur || (r.d.comm ?? -1) > (cur.d.comm ?? -1)
                    || ((r.d.comm ?? -1) === (cur.d.comm ?? -1) && (r.d.daysLeft ?? -1) > (cur.d.daysLeft ?? -1))) best.set(key, r);
            });
            out = [...best.values()];
        }
        const [key, dir] = v.sort.split(':');
        const m = INF_METRICS.find(x => x.key === key);
        if (m) {
            const k = dir === 'asc' ? 1 : -1;
            out = [...out].sort((a, b) => {
                const av = m.get(a.p, a.d), bv = m.get(b.p, b.d);
                const an = av === null || av === undefined, bn = bv === null || bv === undefined;
                if (an && bn) return 0;
                if (an) return 1;
                if (bn) return -1;
                return av < bv ? -k : av > bv ? k : 0;
            });
        }
        if (v.sort === 'default') return out;   // keep the order they were found in, so cards don't jump while loading
        // sorted: rows with full data first, still-loading rows after (stable, keeps the sort inside each group)
        return [...out.filter(r => r.p.cardStatus !== 'loading'), ...out.filter(r => r.p.cardStatus === 'loading')];
    }, [rows, v, queued, failedMap]);
    const pageRows = visible.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
    const toQueue = visible.filter(r => r.d.hasCC && !queued.accept.has(r.p.asin.toUpperCase())).map(r => r.p);
    const toOutreach = visible.filter(r => r.d.ccState !== 'none' && !queued.outreach.has(r.p.asin.toUpperCase())).map(r => r.p);

    const ccCount = rows.filter(r => r.d.hasCC).length;

    const exportRows = (format) => {
        const data = visible.map(({ p, d }) => ({
            ASIN: p.asin,
            Title: p.title || '',
            Brand: p.brand || '',
            Category: p.category || '',
            'CC Status': { live: 'Live', full: 'Full', expired: 'Expired', none: 'No CC' }[d.ccState],
            'CC Campaigns': d.campaignCount,
            'Best Campaign': d.best?.campaignName || '',
            'Commission %': d.comm ?? '',
            'Commission Type': d.hasCC ? 'Creator Connections' : 'Onsite default',
            'Days Left': d.daysLeft ?? '',
            'CC % Full': d.pctFull ?? '',
            'Budget Left': d.budget ?? '',
            'Upper Videos': p.inf?.upperVid ?? '',
            'Upper Brand': p.inf ? brandOf(p.inf.upperVid, p.inf.upperInf) : '',
            'Upper Influencer': p.inf?.upperInf ?? '',
            'Bottom Videos': p.inf?.bottomVid ?? '',
            'Bottom Brand': p.inf ? brandOf(p.inf.bottomVid, p.inf.bottomInf) : '',
            'Bottom Influencer': p.inf?.bottomInf ?? '',
            'Influencer Videos': p.inf?.totalInf ?? '',
            'Sales/Mo': p.monthlySold ?? '',
            Price: p.price ?? '',
            'Revenue/Mo': d.revenue !== null ? d.revenue.toFixed(2) : '',
            Variants: p.variants ?? '',
            'Est. Income/Mo': d.income !== null ? d.income.toFixed(2) : '',
            '12-Mo Profit': d.profit12 !== null ? d.profit12.toFixed(2) : '',
            'Breakeven Days': d.breakevenDays ?? '',
            'My Videos Created': d.videosCreated ?? '',
            'Brand Messaged': d.messaged === true ? 'Yes' : d.messaged === false ? 'No' : '',
            Queued: isQueued(p.asin) ? 'Yes' : 'No',
            'Queue Result': failedMap[p.asin.toUpperCase()] || '',
            'Amazon URL': `https://www.amazon${amazonDomain}/dp/${p.asin}`,
            'Campaign URL': d.best?.campaignId ? ccCampaignUrl(d.best.campaignId) : '',
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const safe = fileName.replace(/[^a-z0-9_-]+/gi, '_').slice(0, 60);
        if (format === 'csv') {
            const blob = new Blob([XLSX.utils.sheet_to_csv(ws)], { type: 'text/csv;charset=utf-8;' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `${safe}.csv`;
            a.click();
            setTimeout(() => URL.revokeObjectURL(a.href), 2000);
        } else {
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Results');
            XLSX.writeFile(wb, `${safe}.xlsx`);
        }
    };

    const inputCls = 'w-full min-w-0 px-2 py-1 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-400';

    return (
        <>
            <PowerSearchTip />
            <div className="bg-white rounded-xl shadow p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative flex-1 min-w-[180px]">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                        <input value={v.search} onChange={e => setV({ search: e.target.value })} placeholder="Search ASIN, title, brand, campaign…"
                            className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-400" />
                    </div>
                    {[['all', `All (${rows.length})`], ['cc', `🤝 Has CC (${ccCount})`], ['nocc', `No CC (${rows.length - ccCount})`]].map(([k, l]) => (
                        <button key={k} onClick={() => setV({ cc: k })}
                            className={`px-3 py-1 rounded-full text-xs font-bold border transition ${v.cc === k ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>{l}</button>
                    ))}
                    <button onClick={() => setShowFilters(s => !s)}
                        className={`flex items-center gap-1 px-3 py-1 rounded-md text-xs font-bold border transition ${activeRanges.length ? 'bg-blue-50 text-blue-700 border-blue-300' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                        <SlidersHorizontal size={13} /> Filters{activeRanges.length ? ` (${activeRanges.length})` : ''}
                    </button>
                    <select value={v.sort} onChange={e => setV({ sort: e.target.value })} className="px-2 py-1 border border-gray-200 rounded-md text-xs">
                        {INF_SORTS.map(([val, l]) => <option key={val} value={val}>{l}</option>)}
                    </select>
                    <button onClick={() => { navigator.clipboard.writeText(visible.map(r => r.p.asin).join('\n')); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                        disabled={!visible.length} className="flex items-center gap-1 px-2.5 py-1 border border-gray-300 rounded-md text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                        {copied ? <Check size={13} className="text-green-600" /> : <Copy size={13} />} {copied ? 'Copied!' : 'Copy ASINs'}
                    </button>
                    {toQueue.length > 0 && (
                        <button
                            onClick={() => { if (window.confirm(`Queue ${toQueue.length} products with CC for "Accept campaign"? It runs next time the extension is opened on desktop.`)) queue('accept', toQueue); }}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                            title="Sends them to the same queue the mobile app uses"
                        >
                            ⚡ Queue accept ({toQueue.length})
                        </button>
                    )}
                    {toOutreach.length > 0 && (
                        <button
                            onClick={() => { if (window.confirm(`Queue ${toOutreach.length} products with CC for "Remind outreach"? It runs next time the extension is opened on desktop.`)) queue('outreach', toOutreach); }}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100"
                            title="Sends them to the same queue the mobile app uses"
                        >
                            🔔 Queue outreach ({toOutreach.length})
                        </button>
                    )}
                    <button onClick={() => exportRows('xlsx')} disabled={!visible.length} className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 disabled:opacity-50"><Download size={13} /> Excel</button>
                    <button onClick={() => exportRows('csv')} disabled={!visible.length} className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100 disabled:opacity-50"><Download size={13} /> CSV</button>
                </div>

                {activeRanges.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5">
                        {activeRanges.map(({ m, text }) => (
                            <span key={m.key} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-blue-50 text-blue-800 border border-blue-200 rounded-full pl-2 pr-1 py-0.5">
                                {text}
                                <button onClick={() => clearRange(m.key)} className="w-4 h-4 rounded-full hover:bg-blue-200 flex items-center justify-center" title="Remove"><X size={10} /></button>
                            </span>
                        ))}
                        {loadingCount > 0 && activeRanges.some(({ m }) => m.full) && (
                            <span className="text-[10px] text-gray-400">Video, income and breakeven filters apply as full data loads</span>
                        )}
                    </div>
                )}

                {showFilters && (
                    <div className="border-t border-gray-100 pt-2">
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1.5">
                            {INF_METRICS.map(m => (
                                <div key={m.key} className="flex items-center gap-1.5 text-xs text-gray-600">
                                    <span className="w-28 flex-shrink-0 truncate" title={m.label}>{m.label}</span>
                                    <input type="number" placeholder="min" value={v.ranges[m.key]?.min ?? ''} onChange={e => setRange(m.key, 'min', e.target.value)} className={inputCls} />
                                    <span className="text-gray-300">–</span>
                                    <input type="number" placeholder="max" value={v.ranges[m.key]?.max ?? ''} onChange={e => setRange(m.key, 'max', e.target.value)} className={inputCls} />
                                </div>
                            ))}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-600">
                            <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={v.hideExpired} onChange={e => setV({ hideExpired: e.target.checked })} className="accent-blue-600" /> Hide expired CC</label>
                            <label className="flex items-center gap-1 cursor-pointer" title="Uses the fill % we already have for a campaign"><input type="checkbox" checked={v.hideFull} onChange={e => setV({ hideFull: e.target.checked })} className="accent-blue-600" /> Hide full CC</label>
                            <select value={v.messaged} onChange={e => setV({ messaged: e.target.value })} className="px-2 py-1 border border-gray-200 rounded-md text-xs">
                                <option value="all">Brand: any</option>
                                <option value="yes">Brand messaged</option>
                                <option value="no">Brand not messaged</option>
                            </select>
                            <select value={v.queue} onChange={e => setV({ queue: e.target.value })} className="px-2 py-1 border border-gray-200 rounded-md text-xs">
                                <option value="all">Queue: any</option>
                                <option value="queued">Queued</option>
                                <option value="not">Not queued</option>
                                <option value="failed">Failed (e.g. campaign full)</option>
                            </select>
                            <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={v.bestPerBrand} onChange={e => setV({ bestPerBrand: e.target.checked })} className="accent-blue-600" /> Best product per brand</label>
                            <button onClick={() => setV0(EMPTY_INF_VIEW)} className="text-gray-400 hover:text-gray-700 font-semibold">Clear all</button>
                        </div>
                    </div>
                )}

                {loadingCount > 0 && (
                    <div className="flex items-center gap-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                        <RefreshCw size={13} className="animate-spin flex-shrink-0" />
                        <span>
                            Loading full data for <strong>{loadingCount}</strong> of {rows.length} products. Numbers fill in as they arrive
                            {activeRanges.some(({ m }) => m.full) ? ', and products that don\'t match your filters drop out.' : '.'}
                        </span>
                        <div className="ml-auto w-28 h-1.5 bg-blue-100 rounded-full overflow-hidden flex-shrink-0">
                            <div className="h-full bg-blue-500 transition-all" style={{ width: `${Math.round((1 - loadingCount / Math.max(rows.length, 1)) * 100)}%` }} />
                        </div>
                    </div>
                )}
                <div className="text-[11px] text-gray-400 text-right">
                    Showing {visible.length} of {rows.length}
                </div>
            </div>

            {visible.length > 0 ? (
                <div className="space-y-3">
                    {pageRows.map(({ p, d }, i) => (
                        <InfluencerCard key={p.asin} p={p} d={d} index={page * PAGE_SIZE + i} amazonDomain={amazonDomain} currency={currency} onRetry={onRetry}
                            queued={{ accept: queued.accept.has(p.asin.toUpperCase()), outreach: queued.outreach.has(p.asin.toUpperCase()), failed: failedMap[p.asin.toUpperCase()] }}
                            onQueue={(type) => queue(type, [p])} />
                    ))}
                    <Pager page={page} total={visible.length} onPage={setPage} />
                </div>
            ) : rows.length > 0 && (
                <div className="text-center text-sm text-gray-500 py-8 bg-white rounded-xl shadow">
                    No products match these filters. Try clearing some, or get more below.
                </div>
            )}
            {rows.length > 0 && (
                <p className="text-[11px] text-gray-400 text-center">
                    Video numbers are real data. Est. income is a rough estimate (monthly sales, price, commission, influencer videos).
                    Profit and breakeven assume you buy the product at its Amazon price. No CC = onsite default commission.
                </p>
            )}
        </>
    );
};